/**
 * Playlist Sync Operation Queue
 *
 * An in-memory queue for playlist sync operations with retry logic.
 * Operations are processed sequentially and retried on failure.
 *
 * Security: Tokens are never sent to the client. Sync operations are
 * handled by the spotify-playlist-sync edge function which retrieves
 * tokens server-side.
 */

import type {
  PlaylistSyncOperation,
  PlaylistSyncOperationType,
  PlaylistSyncEventListener,
} from './types';
import { getPlaylistSyncConfig } from './interface';
import { supabase } from '../supabase';
import { readEdgeFunctionError } from '../musicService/edgeFunctionError';

// Queue configuration
const MAX_RETRY_COUNT = 3;
const RETRY_DELAY_MS = 2000;
const RETRY_BACKOFF_MULTIPLIER = 2;

/**
 * In-memory queue for playlist sync operations.
 */
class PlaylistSyncQueue {
  private queue: PlaylistSyncOperation[] = [];
  private isProcessing = false;
  private listeners: PlaylistSyncEventListener[] = [];

  /**
   * Add an event listener.
   */
  addListener(listener: PlaylistSyncEventListener): void {
    this.listeners.push(listener);
  }

  /**
   * Remove an event listener.
   */
  removeListener(listener: PlaylistSyncEventListener): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  /**
   * Enqueue an operation for processing.
   */
  enqueue(
    type: PlaylistSyncOperationType,
    userId: string,
    trackId: string,
    playlistId: string,
    service: 'spotify' | 'apple_music'
  ): string {
    const operation: PlaylistSyncOperation = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      type,
      userId,
      trackId,
      playlistId,
      service,
      status: 'pending',
      retryCount: 0,
      createdAt: new Date(),
    };

    this.queue.push(operation);
    this.processQueue();

    return operation.id;
  }

  /**
   * Get the current queue status.
   */
  getQueueStatus(): {
    pending: number;
    processing: number;
    failed: number;
  } {
    return {
      pending: this.queue.filter((op) => op.status === 'pending').length,
      processing: this.queue.filter((op) => op.status === 'processing').length,
      failed: this.queue.filter((op) => op.status === 'failed').length,
    };
  }

  /**
   * Clear all failed operations from the queue.
   */
  clearFailed(): void {
    this.queue = this.queue.filter((op) => op.status !== 'failed');
  }

  /**
   * Retry all failed operations.
   */
  retryFailed(): void {
    for (const operation of this.queue) {
      if (operation.status === 'failed') {
        operation.status = 'pending';
        operation.retryCount = 0;
        operation.error = undefined;
      }
    }
    this.processQueue();
  }

  /**
   * Process the queue.
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      while (true) {
        const nextOperation = this.queue.find((op) => op.status === 'pending');
        if (!nextOperation) {
          break;
        }

        await this.processOperation(nextOperation);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Process a single operation.
   *
   * Calls the secure spotify-playlist-sync edge function which handles
   * token retrieval and refresh server-side. Tokens never leave the server.
   */
  private async processOperation(operation: PlaylistSyncOperation): Promise<void> {
    operation.status = 'processing';
    operation.lastAttemptAt = new Date();

    // Check if user is connected (without fetching tokens)
    const config = await getPlaylistSyncConfig(operation.userId);

    if (!config.isConnected) {
      // User is not connected - silently complete
      this.completeOperation(operation);
      return;
    }

    // Only Spotify is supported currently
    if (operation.service !== 'spotify') {
      operation.status = 'failed';
      operation.error = `Service ${operation.service} is not yet supported`;
      this.notifyFailed(operation, operation.error);
      return;
    }

    // Call the secure edge function - tokens are handled server-side
    let data: { success?: boolean; error?: string; code?: string } | null = null;
    let error: Error | null = null;

    try {
      const response = await supabase.functions.invoke('spotify-playlist-sync', {
        body: {
          action: operation.type,
          track_id: operation.trackId,
          playlist_id: operation.playlistId,
        },
      });
      data = response.data;
      error = response.error;
    } catch (err) {
      // Catch network errors, CORS errors, etc.
      error = err instanceof Error ? err : new Error('Network error');
    }

    if (error) {
      // On a non-2xx response the structured body ({ error, code }) lives on the
      // response, not on `data`, so read it from the error context.
      const body = await readEdgeFunctionError(error);
      const errorMessage = body?.error || error.message || 'Sync failed';
      const errorCode = body?.code;
      const isFunctionNotDeployed =
        errorMessage.includes('FunctionsFetchError') ||
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('404') ||
        errorMessage.includes('CORS');

      await this.handleOperationFailure(
        operation,
        errorMessage,
        isFunctionNotDeployed ? 'FUNCTION_NOT_DEPLOYED' : errorCode
      );
      return;
    }

    if (data?.success) {
      this.completeOperation(operation);
    } else {
      await this.handleOperationFailure(
        operation,
        data?.error || 'Unknown error',
        data?.code
      );
    }
  }

  /**
   * Complete an operation successfully.
   */
  private completeOperation(operation: PlaylistSyncOperation): void {
    operation.status = 'completed';
    // Remove from queue
    const index = this.queue.indexOf(operation);
    if (index > -1) {
      this.queue.splice(index, 1);
    }
    this.notifyComplete(operation);
  }

  /**
   * Handle operation failure with retry logic.
   */
  private async handleOperationFailure(
    operation: PlaylistSyncOperation,
    error: string,
    errorCode?: string
  ): Promise<void> {
    operation.error = error;

    // Don't retry for certain error codes. REAUTH_REQUIRED means the refresh
    // token is dead and has been discarded server-side - retrying is pointless
    // until the user reconnects Spotify.
    const noRetryErrors = [
      'NOT_CONNECTED',
      'NOT_FOUND',
      'FUNCTION_NOT_DEPLOYED',
      'REAUTH_REQUIRED',
    ];
    if (errorCode && noRetryErrors.includes(errorCode)) {
      // For NOT_FOUND on remove, just complete it (desired state achieved)
      if (errorCode === 'NOT_FOUND' && operation.type === 'remove') {
        this.completeOperation(operation);
        return;
      }
      // For function not deployed, silently complete - this is an infrastructure
      // issue, not something the user can fix. Don't notify or show errors.
      if (errorCode === 'FUNCTION_NOT_DEPLOYED') {
        // Log once for debugging, then complete silently
        console.debug('Playlist sync function not deployed, skipping sync');
        this.completeOperation(operation);
        return;
      }
      operation.status = 'failed';
      this.notifyFailed(operation, error);
      return;
    }

    // Check if we should retry
    if (operation.retryCount < MAX_RETRY_COUNT) {
      operation.retryCount++;
      operation.status = 'pending';

      // Calculate delay with exponential backoff
      const delay = RETRY_DELAY_MS * Math.pow(RETRY_BACKOFF_MULTIPLIER, operation.retryCount - 1);
      await this.delay(delay);
    } else {
      operation.status = 'failed';
      this.notifyFailed(operation, error);
    }
  }

  /**
   * Delay helper for retry backoff.
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Notify listeners of operation completion.
   */
  private notifyComplete(operation: PlaylistSyncOperation): void {
    for (const listener of this.listeners) {
      listener.onOperationComplete?.(operation);
    }
  }

  /**
   * Notify listeners of operation failure.
   */
  private notifyFailed(operation: PlaylistSyncOperation, error: string): void {
    for (const listener of this.listeners) {
      listener.onOperationFailed?.(operation, error);
    }
  }
}

// Singleton instance
export const playlistSyncQueue = new PlaylistSyncQueue();
